import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { useState } from "react";

import { useViewer } from "@/auth/shared";
import { Button } from "@/components/base/button";
import { title } from "@/components/base/primitives";
import { AddProfileModal } from "@/components/profiles/add-profile-modal";
import { ProfileList } from "@/components/profiles/list";
import { ProfileForm } from "@/components/users/profile-form";
import { getViewerProfilesQueryOptions } from "@/functions/profiles/get-viewer-profiles";
import { useTRPC } from "@/integrations/trpc/react";
import { DefaultLayout } from "@/layouts/default";

export const Route = createFileRoute("/account/setup")({
  head: () => ({
    meta: [{ title: "Account Setup" }],
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const viewer = useViewer();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: viewerProfiles } = useSuspenseQuery(
    getViewerProfilesQueryOptions(),
  );

  const hasProfile = Boolean(viewerProfiles?.length);
  const [isAddingProfile, setIsAddingProfile] = useState(false);

  const finishSetup = async (to: string) => {
    await queryClient.invalidateQueries(trpc.viewer.get.queryOptions());
    navigate({ to });
  };

  // Pre-fill profile form with name from sign-up
  const nameParts = viewer?.name.split(" ") ?? [];
  const initialFirstName = nameParts[0] ?? "";
  const initialLastName =
    nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

  return (
    <DefaultLayout>
      <div className="mx-auto flex max-w-xl flex-col space-y-6 text-center">
        <h1 className={title({ size: "lg" })}>Account Setup</h1>
      </div>

      <div className="mx-auto max-w-lg rounded-md bg-white p-8">
        <h2 className="mb-8 text-center text-2xl font-bold">
          Step 3:{" "}
          {hasProfile ? "Create Another Profile" : "Create Your Profile"}
        </h2>

        <div className="space-y-4">
          <div className="space-y-3">
            {!hasProfile && (
              <>
                <p className="mx-auto w-8/10 text-center">
                  Create profiles for yourself and/or each of your children who
                  will be playing.
                </p>

                <ProfileForm
                  initialFirstName={initialFirstName}
                  initialLastName={initialLastName}
                />
              </>
            )}

            {hasProfile && viewerProfiles && (
              <>
                <hr className="border-gray-300" />

                <ProfileList
                  profiles={viewerProfiles}
                  linkNames={false}
                  showActions={false}
                />

                <div className="flex flex-col items-stretch gap-y-2">
                  <Button
                    variant="outline"
                    color="primary"
                    onPress={() => setIsAddingProfile(true)}
                  >
                    <PlusIcon />
                    <span>Add Profile</span>
                  </Button>

                  <Button
                    color="primary"
                    className="w-full"
                    onPress={() => finishSetup("/account")}
                  >
                    Go to your account
                  </Button>

                  <Button
                    variant="link"
                    color="primary"
                    className="text-sm"
                    onPress={() => finishSetup("/account/registrations")}
                  >
                    Go to Tournament Registration
                  </Button>
                </div>
              </>
            )}

            <AddProfileModal
              isOpen={isAddingProfile}
              onOpenChange={setIsAddingProfile}
            />
          </div>
        </div>
      </div>
    </DefaultLayout>
  );
}
